import React from 'react';
import { motion } from 'framer-motion';
import { Mic, Bell, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../components/PageTransition';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SPOTIFY_SHOW_URL } from '../data/season1Episodes';
const whatsComing = [{
  title: 'New Voices',
  desc: 'Fresh guests from every walk of life, sharing what faith looks like when it costs something.',
  icon: <Users className="w-6 h-6 text-gold-500" data-id="element-763" data-mp="sq87grd5w5ki" />
}, {
  title: 'Deeper Conversations',
  desc: 'Longer sit-downs, follow-up episodes, and honest questions we did not get to in Season 1.',
  icon: <Mic className="w-6 h-6 text-gold-500" data-id="element-764" data-mp="jrwb08pm6dk5" />
}, {
  title: 'Same Heart',
  desc: 'No polish, just truth. Real stories of grace in the process, anchored in Scripture.',
  icon: <Bell className="w-6 h-6 text-gold-500" data-id="element-765" data-mp="9s5gw84yu0kl" />
}];
export function Season2Page() {
  return <PageTransition data-id="element-766" data-mp="9nomwd3imkak">
      <div className="pt-24 pb-16 min-h-screen" data-id="element-767" data-mp="9n6l70y33dv9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-768" data-mp="eeoklkkgl6e4">
          {/* Hero */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-16" data-id="element-769" data-mp="eghvjc23ucrx">
            <span className="inline-block bg-gold-500 text-dark-900 px-4 py-1.5 rounded-full font-bold text-sm shadow-lg mb-6" data-id="element-770" data-mp="sfhj0gdvslmi">
              Coming Soon
            </span>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-771" data-mp="igiffc7z3g6d">
              Season 2
            </h1>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full mb-6" data-id="element-772" data-mp="nebpigcecubx"></div>
            <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed" data-id="element-773" data-mp="335zftswwnolr">
              Season 1 is complete — twenty-one stories of faith, transformation,
              and grace. Season 2 arrives Fall 2026 with new guests and the same
              honest conversations.
            </p>
          </motion.div>

          {/* Placeholder Episode Slate */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="rounded-2xl border border-gold-500/30 bg-dark-800 p-10 md:p-16 text-center mb-16" data-id="element-774" data-mp="19q4n843e8vyd">
            <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-gold-500/10 border-2 border-gold-500 flex items-center justify-center" data-id="element-775" data-mp="qvzb3t6tyuc0">
              <Mic className="w-12 h-12 text-gold-500" data-id="element-776" data-mp="gzcu5fhmknf9" />
            </div>
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-4" data-id="element-777" data-mp="h17ccm1ii1fn">
              Episodes drop Fall 2026
            </h2>
            <p className="text-white/70 max-w-xl mx-auto mb-8" data-id="element-778" data-mp="2bcuiv5trnqbv">
              We're recording now. As soon as the first Season 2 episode is
              live, it will show up right here.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto" data-id="element-779" data-mp="2nuy9hwkld5ix">
              {[1, 2, 3].map(slot => <div key={slot} className="rounded-xl border border-dashed border-dark-500 bg-dark-700 p-6" data-id="element-780" data-mp="2smydc03s8iks">
                  <p className="text-white/30 text-xs font-mono mb-3" data-id="element-781" data-mp="1zyki1s42w0cx">
                    EP {slot}
                  </p>
                  <div className="h-2.5 w-3/4 mx-auto bg-dark-600 rounded-full mb-2.5" data-id="element-782" data-mp="24xnc6hiism64"></div>
                  <div className="h-2.5 w-1/2 mx-auto bg-dark-600 rounded-full" data-id="element-783" data-mp="29v5n2dg3lbgr"></div>
                </div>)}
            </div>
          </motion.div>

          {/* What's Coming */}
          <div className="mb-16" data-id="element-784" data-mp="3la2fzyfekkte">
            <h2 className="text-3xl font-serif font-bold text-white text-center mb-10" data-id="element-785" data-mp="3ld84bbg7jio2">
              What's Coming
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-786" data-mp="3nva2swr9y228">
              {whatsComing.map((item, index) => <motion.div key={item.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} data-id="element-787" data-mp="3juq5nt6payg3" data-mpi={index}>
                  <Card className="h-full bg-dark-700 border-dark-600 p-8 text-center" data-id="element-788" data-mp="kejrq3gu4yto">
                    <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-dark-600 border border-dark-400 flex items-center justify-center" data-id="element-789" data-mp="ffqiwochtvzu">
                      {item.icon}
                    </div>
                    <h3 className="text-xl font-serif font-bold text-white mb-3" data-id="element-790" data-mp="pe67oko1st66">
                      {item.title}
                    </h3>
                    <p className="text-white/70 leading-relaxed" data-id="element-791" data-mp="19110e747x0wr">{item.desc}</p>
                  </Card>
                </motion.div>)}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-dark-800 rounded-2xl p-8 md:p-12 text-center border border-dark-600" data-id="element-792" data-mp="z0qqavdhc9lt">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3" data-id="element-793" data-mp="13zbs2z8dr1cd">
              Know someone who should be on the podcast?
            </h2>
            <p className="text-white/70 mb-8 max-w-xl mx-auto" data-id="element-794" data-mp="u2yw1upd4beg">
              We're booking guests for Season 2 right now. Drop us a note and
              tell us their story.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-795" data-mp="l9vlpva0keo1">
              <Link to="/guests" data-id="element-796" data-mp="l7r7ajehnx9i">
                <Button rightIcon={<ArrowRight className="w-4 h-4" data-id="element-798" data-mp="6dw8k681roc1" />} data-id="element-797" data-mp="6cl1nxjb20gp">
                  Meet Our Guests
                </Button>
              </Link>
              <Link to="/podcasts/season-1" data-id="element-799" data-mp="be20lni8bcun">
                <Button variant="outline" data-id="element-800" data-mp="3od8foqcfk3ow">Catch Up on Season 1</Button>
              </Link>
              <a href={SPOTIFY_SHOW_URL} target="_blank" rel="noopener noreferrer" data-id="element-801" data-mp="3jlgqok8xa60v">
                <Button variant="ghost" data-id="element-802" data-mp="3en6uc6bgozdo">Follow on Spotify</Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>;
}